import React, { useState, useEffect } from 'react';
import './WorkPlanning.css';
import firebaseAuthService from '../services/firebaseAuthService';
import universalDataService from '../services/universalDataService';

const WorkPlanning = ({ data, onWorkplanUpdate }) => {
  // State for work plans
  const [workPlans, setWorkPlans] = useState([]);
  const [showAddPlanForm, setShowAddPlanForm] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [workers, setWorkers] = useState([]);
  
  // State for new work plan
  const [newWorkPlan, setNewWorkPlan] = useState({
    id: '',
    fieldId: '',
    taskType: 'plowing',
    description: '',
    assignedTo: '',
    scheduledDate: '',
    priority: 'medium',
    status: 'planned',
    materials: [],
    equipment: []
  });
  
  // State for material/equipment selection in new plan
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [materialQuantities, setMaterialQuantities] = useState({});
  
  // Task types that can be planned
  const taskTypes = [
    { id: 'plowing', name: 'Plowing' },
    { id: 'planting', name: 'Planting' },
    { id: 'fertilizing', name: 'Fertilizing' },
    { id: 'spraying', name: 'Spraying' },
    { id: 'irrigating', name: 'Irrigating' },
    { id: 'harvesting', name: 'Harvesting' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'other', name: 'Other' }
  ];
  
  // Priority options
  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];
  
  // Status options
  const statuses = [
    { value: 'planned', label: 'Planned' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'inProgress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];
  
  const currentSession = firebaseAuthService.getCurrentSession();
  
  // Load data on component mount
  useEffect(() => {
    if (!currentSession?.farm?.id) return;
    
    const farmId = currentSession.farm.id;
    
    // Load farm-specific data
    const savedMaterials = localStorage.getItem(`agrimanage-materials_${farmId}`);
    const savedEquipment = localStorage.getItem(`agrimanage-equipments_${farmId}`);
    const savedWorkPlans = localStorage.getItem(`agrimanage-workplans_${farmId}`);
    const savedWorkers = localStorage.getItem(`agrimanage-workers_${farmId}`);
    
    if (savedMaterials) {
      setAvailableMaterials(JSON.parse(savedMaterials));
    }
    if (savedEquipment) {
      setAvailableEquipment(JSON.parse(savedEquipment));
    }
    if (savedWorkPlans) {
      setWorkPlans(JSON.parse(savedWorkPlans));
    }
    if (savedWorkers) {
      setWorkers(JSON.parse(savedWorkers));
    } else {
      // Load workers from Firebase user management
      loadFarmWorkers();
    }
  }, [currentSession]);
  
  // Load workers from Firebase
  const loadFarmWorkers = async () => {
    if (!currentSession?.farm?.id) return;
    
    try {
      const farmUsers = await firebaseAuthService.getFarmUsers(currentSession.farm.id);
      const workersData = farmUsers
        .filter(user => user.role === 'worker')
        .map(user => ({
          id: user.uid,
          name: user.name,
          role: 'Worker',
          completedTasks: 0,
          workedHours: 0
        }));
      
      setWorkers(workersData);
      
      // Save to localStorage
      const farmId = currentSession.farm.id;
      localStorage.setItem(`agrimanage-workers_${farmId}`, JSON.stringify(workersData));
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };
  
  // Save work plans when they change
  useEffect(() => {
    if (!currentSession?.farm?.id || workPlans.length === 0) return;
    
    const farmId = currentSession.farm.id;
    localStorage.setItem(`agrimanage-workplans_${farmId}`, JSON.stringify(workPlans));
    
    // Update parent component if callback provided
    if (onWorkplanUpdate) {
      onWorkplanUpdate(workPlans);
    }
  }, [workPlans, onWorkplanUpdate, currentSession]);
  
  // Filter materials based on task type
  const getRelevantMaterials = () => {
    switch (newWorkPlan.taskType) {
      case 'planting':
        return availableMaterials.filter(m => m.type === 'seeds');
      case 'fertilizing':
        return availableMaterials.filter(m => m.type === 'fertilizer');
      case 'spraying':
        return availableMaterials.filter(m => m.type === 'herbicide');
      default:
        return availableMaterials;
    }
  };
  
  // Filter equipment based on task type
  const getRelevantEquipment = () => {
    switch (newWorkPlan.taskType) {
      case 'plowing':
        return availableEquipment.filter(e => ['tractor', 'plow'].includes(e.type));
      case 'planting':
        return availableEquipment.filter(e => ['tractor', 'planter'].includes(e.type));
      case 'spraying':
        return availableEquipment.filter(e => ['tractor', 'sprayer'].includes(e.type));
      case 'harvesting':
        return availableEquipment.filter(e => ['harvester', 'tractor', 'trailer'].includes(e.type));
      default:
        return availableEquipment;
    }
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewWorkPlan(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Toggle material selection
  const handleMaterialToggle = (materialId) => {
    setSelectedMaterials(prev => {
      if (prev.includes(materialId)) {
        // Remove from selected materials
        const newSelected = prev.filter(id => id !== materialId);
        
        // Remove quantity data
        setMaterialQuantities(prevQuantities => {
          const newQuantities = {...prevQuantities};
          delete newQuantities[materialId];
          return newQuantities;
        });
        
        return newSelected;
      } else {
        return [...prev, materialId];
      }
    });
  };
  
  // Update material quantity
  const handleMaterialQuantityChange = (materialId, quantity) => {
    setMaterialQuantities(prev => ({
      ...prev,
      [materialId]: quantity
    }));
  };
  
  // Toggle equipment selection
  const handleEquipmentToggle = (equipmentId) => {
    setSelectedEquipment(prev => {
      if (prev.includes(equipmentId)) {
        return prev.filter(id => id !== equipmentId);
      } else {
        return [...prev, equipmentId];
      }
    });
  };
  
  // Handle adding new work plan
  const handleAddWorkPlan = (e) => {
    e.preventDefault();
    
    // Format materials with quantities
    const materialsWithQuantities = selectedMaterials.map(materialId => {
      const material = availableMaterials.find(m => m.id === materialId);
      return {
        id: materialId,
        name: material.name,
        type: material.type,
        quantity: materialQuantities[materialId] || '0',
        unit: material.unit
      };
    });
    
    // Format equipment
    const selectedEquipmentDetails = selectedEquipment.map(equipId => {
      const equipment = availableEquipment.find(e => e.id === equipId);
      return {
        id: equipId,
        name: equipment.name,
        type: equipment.type
      };
    });
    
    const id = `plan-${Date.now()}`;
    const workPlanToAdd = {
      ...newWorkPlan,
      id,
      materials: materialsWithQuantities,
      equipment: selectedEquipmentDetails,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setWorkPlans(prev => [...prev, workPlanToAdd]);
    
    // Reset form
    setNewWorkPlan({
      id: '',
      fieldId: '',
      taskType: 'plowing',
      description: '',
      assignedTo: '',
      scheduledDate: '',
      priority: 'medium',
      status: 'planned',
      materials: [],
      equipment: []
    });
    setSelectedMaterials([]);
    setSelectedEquipment([]);
    setMaterialQuantities({});
    setShowAddPlanForm(false);
  };
  
  // Handle updating work plan status
  const handleUpdateStatus = (planId, newStatus) => {
    setWorkPlans(prev => 
      prev.map(plan => 
        plan.id === planId 
          ? { 
              ...plan, 
              status: newStatus,
              updatedAt: new Date().toISOString()
            } 
          : plan
      )
    );
  };
  
  // Get field name by ID
  const getFieldName = (fieldId) => {
    const field = data?.fields?.find(f => f.id === fieldId);
    return field ? field.name : 'Unknown Field';
  };
  
  // Get worker name by ID
  const getWorkerName = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    return worker ? worker.name : 'Unknown Worker';
  };
  
  // Filter work plans based on active tab
  const filteredWorkPlans = (Array.isArray(workPlans) ? workPlans : []).filter(plan => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const planDate = new Date(plan.scheduledDate);
    planDate.setHours(0, 0, 0, 0);
    
    switch (activeTab) {
      case 'upcoming':
        return planDate >= today && ['planned', 'assigned'].includes(plan.status);
      case 'inProgress':
        return plan.status === 'inProgress';
      case 'completed':
        return plan.status === 'completed';
      case 'all':
        return true;
      default:
        return false;
    }
  });
  
  // Load work plans from storage
  const loadWorkPlans = () => {
    try {
      const savedData = localStorage.getItem('workPlans');
      const parsedData = savedData ? JSON.parse(savedData) : [];
      setWorkPlans(Array.isArray(parsedData) ? parsedData : []);
    } catch (error) {
      console.error('Error loading work plans:', error);
      setWorkPlans([]);
    }
  };

  // Load workers from storage
  const loadWorkers = () => {
    try {
      const savedWorkers = localStorage.getItem('workers');
      const parsedWorkers = savedWorkers ? JSON.parse(savedWorkers) : [];
      setWorkers(Array.isArray(parsedWorkers) ? parsedWorkers : []);
    } catch (error) {
      console.error('Error loading workers:', error);
      setWorkers([]);
    }
  };
  
  // Load work plans and workers on component mount
  useEffect(() => {
    loadWorkPlans();
    loadWorkers();
    
    // Listen for cloud sync completion - FORCE RELOAD
    const handleCloudSyncComplete = (event) => {
      console.log('📱 Cloud sync completed - FORCE RELOADING WorkPlanning data');
      setTimeout(() => {
        loadWorkPlans();
        loadWorkers();
      }, 500); // Small delay to ensure data is written
    };
    
    window.addEventListener('cloudSyncComplete', handleCloudSyncComplete);
    window.addEventListener('workPlansDataUpdated', handleCloudSyncComplete);
    
    return () => {
      window.removeEventListener('cloudSyncComplete', handleCloudSyncComplete);
      window.removeEventListener('workPlansDataUpdated', handleCloudSyncComplete);
    };
  }, []);
  
  return (
    <div className="work-planning-container">
      <h2>Work Planning & Management</h2>
      
      <div className="work-planning-header">
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => handleTabChange('upcoming')}
          >
            Upcoming
          </button>
          <button 
            className={`tab-button ${activeTab === 'inProgress' ? 'active' : ''}`}
            onClick={() => handleTabChange('inProgress')}
          >
            In Progress
          </button>
          <button 
            className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => handleTabChange('completed')}
          >
            Completed
          </button>
          <button 
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All Work
          </button>
        </div>
        
        <button 
          className="add-plan-button"
          onClick={() => setShowAddPlanForm(true)}
        >
          Plan New Work
        </button>
      </div>
      
      {/* Work Plans Table */}
      <div className="work-plans-table-container">
        <table className="work-plans-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Task</th>
              <th>Description</th>
              <th>Assigned To</th>
              <th>Scheduled Date</th>
              <th>Priority</th>
              <th>Materials</th>
              <th>Equipment</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkPlans.map(plan => (
              <tr key={plan.id} className={`priority-${plan.priority}`}>
                <td>{getFieldName(plan.fieldId)}</td>
                <td>{taskTypes.find(t => t.id === plan.taskType)?.name || plan.taskType}</td>
                <td>{plan.description}</td>
                <td>{getWorkerName(plan.assignedTo)}</td>
                <td>{new Date(plan.scheduledDate).toLocaleDateString()}</td>
                <td>
                  <span className={`priority-badge ${plan.priority}`}>
                    {plan.priority.charAt(0).toUpperCase() + plan.priority.slice(1)}
                  </span>
                </td>
                <td>
                  {plan.materials && plan.materials.length > 0 ? 
                    plan.materials.map(m => 
                      `${m.name}: ${m.quantity} ${m.unit}`
                    ).join(', ') : 
                    'None'
                  }
                </td>
                <td>
                  {plan.equipment && plan.equipment.length > 0 ? 
                    plan.equipment.map(e => e.name).join(', ') : 
                    'None'
                  }
                </td>
                <td>
                  <select 
                    value={plan.status}
                    onChange={(e) => handleUpdateStatus(plan.id, e.target.value)}
                    className={`status-select status-${plan.status}`}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <button className="action-button">Edit</button>
                  <button className="action-button delete">Delete</button>
                </td>
              </tr>
            ))}
            {filteredWorkPlans.length === 0 && (
              <tr>
                <td colSpan="10" className="empty-message">
                  No work plans to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Worker Performance Section */}
      <div className="worker-performance-section">
        <h3>Worker Performance</h3>
        <div className="worker-cards">
          {workers.map(worker => (
            <div key={worker.id} className="worker-card">
              <div className="worker-avatar">
                {worker.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="worker-info">
                <h4 className="worker-name">{worker.name}</h4>
                <p className="worker-role">{worker.role}</p>
                <div className="worker-stats">
                  <div className="stat">
                    <span className="stat-value">{worker.completedTasks}</span>
                    <span className="stat-label">Tasks Completed</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{worker.workedHours}</span>
                    <span className="stat-label">Hours Worked</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Add Work Plan Form */}
      {showAddPlanForm && (
        <div className="form-overlay">
          <div className="form-container work-plan-form">
            <h3>Plan New Work</h3>
            <form onSubmit={handleAddWorkPlan}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fieldId">Field</label>
                  <select 
                    id="fieldId" 
                    name="fieldId" 
                    value={newWorkPlan.fieldId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a field</option>
                    {data.fields && data.fields.map(field => (
                      <option key={field.id} value={field.id}>{field.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="taskType">Task Type</label>
                  <select 
                    id="taskType" 
                    name="taskType" 
                    value={newWorkPlan.taskType}
                    onChange={handleInputChange}
                    required
                  >
                    {taskTypes.map(task => (
                      <option key={task.id} value={task.id}>{task.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="assignedTo">Assign To</label>
                  <select 
                    id="assignedTo" 
                    name="assignedTo" 
                    value={newWorkPlan.assignedTo}
                    onChange={handleInputChange}
                  >
                    <option value="">Unassigned</option>
                    {workers.map(worker => (
                      <option key={worker.id} value={worker.id}>{worker.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="scheduledDate">Scheduled Date</label>
                  <input 
                    type="date" 
                    id="scheduledDate" 
                    name="scheduledDate" 
                    value={newWorkPlan.scheduledDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="priority">Priority</label>
                  <select 
                    id="priority" 
                    name="priority" 
                    value={newWorkPlan.priority}
                    onChange={handleInputChange}
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>{priority.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="status">Initial Status</label>
                  <select 
                    id="status" 
                    name="status" 
                    value={newWorkPlan.status}
                    onChange={handleInputChange}
                  >
                    <option value="planned">Planned</option>
                    <option value="assigned">Assigned</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea 
                  id="description" 
                  name="description" 
                  value={newWorkPlan.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter detailed instructions for this work..."
                  required
                />
              </div>
              
              {/* Materials Selection */}
              {['planting', 'fertilizing', 'spraying'].includes(newWorkPlan.taskType) && (
                <div className="form-group">
                  <label>Required Materials</label>
                  <div className="materials-selection">
                    {getRelevantMaterials().length > 0 ? (
                      getRelevantMaterials().map(material => (
                        <div 
                          key={material.id} 
                          className={`material-item ${selectedMaterials.includes(material.id) ? 'selected' : ''}`}
                        >
                          <div 
                            className="material-header"
                            onClick={() => handleMaterialToggle(material.id)}
                          >
                            <span className="material-name">{material.name}</span>
                            <span className="material-type">{material.type}</span>
                            <span className="material-stock">In stock: {material.quantity} {material.unit}</span>
                          </div>
                          
                          {selectedMaterials.includes(material.id) && (
                            <div className="material-quantity">
                              <input 
                                type="number" 
                                min="0" 
                                max={material.quantity}
                                step="0.1"
                                placeholder={`Quantity (${material.unit})`}
                                value={materialQuantities[material.id] || ''}
                                onChange={(e) => handleMaterialQuantityChange(material.id, e.target.value)}
                              />
                              <span className="unit">{material.unit}</span>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="empty-message">No relevant materials in inventory</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Equipment Selection */}
              <div className="form-group">
                <label>Required Equipment</label>
                <div className="equipment-selection">
                  {getRelevantEquipment().length > 0 ? (
                    getRelevantEquipment().map(equipment => (
                      <div 
                        key={equipment.id} 
                        className={`equipment-item ${selectedEquipment.includes(equipment.id) ? 'selected' : ''} ${equipment.status !== 'operational' ? 'unavailable' : ''}`}
                        onClick={() => equipment.status === 'operational' && handleEquipmentToggle(equipment.id)}
                      >
                        <span className="equipment-name">{equipment.name}</span>
                        <span className="equipment-type">{equipment.type}</span>
                        <span className={`equipment-status status-${equipment.status}`}>
                          {equipment.status.charAt(0).toUpperCase() + equipment.status.slice(1)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-message">No relevant equipment in inventory</p>
                  )}
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowAddPlanForm(false)} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Create Work Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkPlanning; 